import { EVIDENCE_ROUTE_PATHS } from '../constants/evidenceRoutePaths';
import { navigateWithFilters } from './evidenceRouteFilterHelper';
import { CORRECTION_STATUS } from '../constants/correctionStatuses';
import { CORRECTION_REQUESTS_SECTION_KEY } from '../constants/evidenceDashboardConstants';

export const isCorrectionsRow = (row) =>
  row.some((section) => section.key === CORRECTION_REQUESTS_SECTION_KEY);

export const getOverdueCheckoutSeverityClass = (daysOverdue) => {
  if (daysOverdue > 30) return 'is-critical';
  if (daysOverdue > 14) return 'is-elevated';
  return 'is-warning';
};

export const getApproachingActionSeverityClass = (daysUntil) => {
  if (daysUntil < 0) return 'is-past';
  if (daysUntil <= 7) return 'is-urgent';
  if (daysUntil <= 30) return 'is-soon';
  return 'is-far';
};

export const getApproachingActionDaysLabel = (daysUntil) => {
  if (daysUntil < 0) return `${Math.abs(daysUntil)}d overdue`;
  if (daysUntil === 0) return 'Today';
  return `${daysUntil}d`;
};

export const getStatusIdsByCategory = (custodyStatuses, category) =>
  (custodyStatuses || [])
    .filter((status) => (status.Category || '').toLowerCase() === category.toLowerCase())
    .map((status) => status.PtsCodeID);

export const buildOverdueActionDateFilter = () => ({
  from: null,
  to: new Date().toISOString(),
});

// correctionStatusOptions is the getRefValues(Category='CorrectionRequest') list — its numeric
// Ids are environment-specific, so "open" is resolved by matching the stable Code text instead of
// a hardcoded Id.
export const getCorrectionOpenStatusIds = (correctionStatusOptions) =>
  (correctionStatusOptions || [])
    .filter(
      (status) =>
        status.code === CORRECTION_STATUS.OPEN || status.code === CORRECTION_STATUS.IN_PROGRESS
    )
    .map((status) => status.value);

export const getOverdueAuditTitle = (summary) => {
  const days = summary?.overdueAudit?.days;
  return days > 0 ? `Overdue Audit (>${days}d)` : 'Overdue Audit';
};

const AGENCY_STACKED_BAR_WIDTH = 200;

export const buildAgencyStackedBarSegments = (agency) => {
  const total = agency?.total || 0;
  if (!total) return [];

  const parts = [
    { value: agency.in, colorClass: 'in' },
    { value: agency.out, colorClass: 'out' },
    { value: agency.waiting, colorClass: 'waiting' },
    { value: agency.disposed, colorClass: 'disposed' },
  ];

  let x = 0;
  return parts
    .filter((part) => part.value > 0)
    .map((part) => {
      const width = (part.value / total) * AGENCY_STACKED_BAR_WIDTH;
      const segment = { x, width, colorClass: part.colorClass };
      x += width;
      return segment;
    });
};

export const getInventoryHealthValueColor = (key, value) => {
  if (key === 'avgDaysInEvidence') {
    if (value > 180) return 'danger';
    if (value > 90) return 'warning';
    return 'success';
  }

  if (key === 'overdueActions') return value > 0 ? 'danger' : 'success';
  if (key === 'noCustody') return value > 0 ? 'warning' : 'success';

  return 'primary';
};

export const resolveDashboardNavigation = (
  navigateTo,
  { history, custodyStatuses, correctionStatusOptions, id, label } = {}
) => {
  if (!navigateTo || !history) return;

  if (navigateTo === 'disposal') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.disposal, {});
    return;
  }

  if (navigateTo === 'audit') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.audit, {});
    return;
  }

  if (navigateTo === 'recordsAll') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.records, {});
    return;
  }

  if (navigateTo === 'recordsOverdueActionDate') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.records, {
      requiredActionDate: buildOverdueActionDateFilter(),
    });
    return;
  }

  if (navigateTo === 'recordsOverdueReturn') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.records, { overdue: true });
    return;
  }

  if (navigateTo.startsWith('recordsStatusCategory:')) {
    const category = navigateTo.split(':')[1];
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.records, {
      status: getStatusIdsByCategory(custodyStatuses, category),
    });
    return;
  }

  if (navigateTo === 'recordsNotDisposed') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.records, {
      status: [
        ...getStatusIdsByCategory(custodyStatuses, 'IN'),
        ...getStatusIdsByCategory(custodyStatuses, 'OUT'),
        ...getStatusIdsByCategory(custodyStatuses, 'Waiting'),
      ],
    });
    return;
  }

  if (navigateTo === 'recordsCorrectionAny') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.records, { hasCorrection: 'any' });
    return;
  }

  if (navigateTo === 'recordsCorrectionOpen') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.records, { hasCorrection: 'open' });
    return;
  }

  if (navigateTo === 'recordsCorrectionResolved') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.records, { hasCorrection: 'resolved' });
    return;
  }

  if (id === null || id === undefined) return;

  if (navigateTo === 'recordsCategory') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.records, { category: [id] });
    return;
  }

  if (navigateTo === 'recordsRequiredAction') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.records, { requiredAction: [id] });
    return;
  }

  if (navigateTo === 'recordsSubmittedBy') {
    navigateWithFilters(
      history,
      EVIDENCE_ROUTE_PATHS.records,
      { submittedBy: [id] },
      label ? { submittedBy: { [id]: label } } : undefined
    );
    return;
  }

  if (navigateTo === 'recordsTempLocation') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.records, {
      tempLocation: [id],
      status: getStatusIdsByCategory(custodyStatuses, 'Waiting'),
    });
    return;
  }

  if (navigateTo === 'recordsStorageLocation') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.records, {
      StorageLocation: [id],
      status: getStatusIdsByCategory(custodyStatuses, 'IN'),
    });
    return;
  }

  if (navigateTo === 'recordsCorrectionByReason') {
    navigateWithFilters(history, EVIDENCE_ROUTE_PATHS.records, {
      hasCorrection: 'open',
      correctionStatus: getCorrectionOpenStatusIds(correctionStatusOptions),
      correctionReason: [id],
    });
    return;
  }

  if (navigateTo === 'recordsCorrectionByRoutedTo') {
    navigateWithFilters(
      history,
      EVIDENCE_ROUTE_PATHS.records,
      {
        hasCorrection: 'open',
        correctionStatus: getCorrectionOpenStatusIds(correctionStatusOptions),
        correctionRoutedTo: [id],
      },
      label ? { correctionRoutedTo: { [id]: label } } : undefined
    );
  }
};
